import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { useListDocuments } from '@/hooks/useDocuments';
import { useDocumentDownloadUrl } from '@/hooks/useDocuments';
import { useStaticMapUrl } from '@/hooks/useStaticMapUrl';
import { Building, Camera, MapPin, Star } from 'lucide-react';
import type React from 'react';
import { Link } from 'react-router-dom';

import { formatCurrency } from '@/lib/utils';
import type { Asset } from '@/types/asset';

interface AssetCardProps {
  asset: Asset;
}

// Component to fetch and display the cover photo (or first photo) for an asset, with photo count badge
const AssetPhotoThumbnail: React.FC<{
  assetId: number;
  address?: string | null;
}> = ({ assetId, address }) => {
  const {
    data: photos,
    isLoading,
    error,
  } = useListDocuments({ asset_id: assetId, tags: ['photo'] });

  // Find cover photo (tagged 'cover'), else first photo
  const coverPhoto =
    photos?.find((p) => (p.tags || []).includes('cover')) || photos?.[0];
  const photoCount = photos?.length || 0;

  const { data: photoUrl, isLoading: photoLoading } = useDocumentDownloadUrl(
    coverPhoto?.id,
    { enabled: !!coverPhoto?.id },
  );

  // Get static map URL if no photos and address is available
  const {
    data: mapUrl,
    isLoading: mapLoading,
    error: mapError,
  } = useStaticMapUrl({
    address,
    enabled: !coverPhoto && !photoLoading && !isLoading,
  });

  // Debug logging
  console.log('AssetPhotoThumbnail debug:', {
    assetId,
    address,
    hasPhotos: !!coverPhoto,
    photoLoading,
    isLoading,
    mapUrl,
    mapLoading,
    mapError,
  });

  const isImageFile = (fileName: string): boolean => {
    return /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName);
  };

  // Show loading state
  if (isLoading || photoLoading) {
    return (
      <div className="w-full h-48 bg-muted rounded-t-lg flex items-center justify-center">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
      </div>
    );
  }

  // Show photo if available and it's an image
  if (coverPhoto && isImageFile(coverPhoto.original_name) && photoUrl) {
    return (
      <div className="w-full h-48 bg-muted rounded-t-lg overflow-hidden relative">
        <img
          src={photoUrl}
          alt={coverPhoto.original_name}
          className="w-full h-full object-cover"
        />
        {/* Photo count badge */}
        {photoCount > 0 && (
          <span className="absolute top-2 right-2 bg-black/70 text-white text-xs rounded-full px-2 py-0.5 flex items-center gap-1">
            <Camera className="h-3 w-3 mr-1" />
            {photoCount}
          </span>
        )}
        {/* Cover badge */}
        {(coverPhoto.tags || []).includes('cover') && (
          <span className="absolute top-2 left-2 bg-yellow-400 text-white rounded-full px-2 py-0.5 text-xs flex items-center gap-1 shadow">
            <Star className="h-3 w-3" /> Cover
          </span>
        )}
      </div>
    );
  }

  // Show Google Maps image if no photos but map URL is available
  if (!coverPhoto && mapUrl && !mapLoading) {
    return (
      <div className="w-full h-48 bg-muted rounded-t-lg overflow-hidden relative">
        <img
          src={mapUrl}
          alt="Property location"
          className="w-full h-full object-cover"
          onError={(e) => {
            console.error('Failed to load Google Maps image:', e);
            // Hide the image on error
            e.currentTarget.style.display = 'none';
          }}
        />
        {/* Map indicator badge */}
        <span className="absolute top-2 right-2 bg-blue-600 text-white text-xs rounded-full px-2 py-0.5 flex items-center gap-1">
          <MapPin className="h-3 w-3 mr-1" />
          Map
        </span>
        {/* Photo count badge (if there are non-image photos) */}
        {photoCount > 0 && (
          <span className="absolute top-2 left-2 bg-black/70 text-white text-xs rounded-full px-2 py-0.5 flex items-center gap-1">
            <Camera className="h-3 w-3 mr-1" />
            {photoCount}
          </span>
        )}
      </div>
    );
  }

  // Show loading for map
  if (mapLoading) {
    return (
      <div className="w-full h-48 bg-muted rounded-t-lg flex items-center justify-center">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
      </div>
    );
  }

  // Show fallback icon if no photos or map
  return (
    <div className="w-full h-48 bg-muted rounded-t-lg flex items-center justify-center relative">
      <div className="text-center">
        <Building className="h-12 w-12 mx-auto text-muted-foreground mb-2" />
        <p className="text-xs text-muted-foreground">No photo</p>
      </div>
      {/* Photo count badge (if there are non-image photos) */}
      {photoCount > 0 && (
        <span className="absolute top-2 right-2 bg-black/70 text-white text-xs rounded-full px-2 py-0.5 flex items-center gap-1">
          <Camera className="h-3 w-3 mr-1" />
          {photoCount}
        </span>
      )}
    </div>
  );
};

export const AssetCard: React.FC<AssetCardProps> = ({ asset }) => {
  return (
    <Link to={`/app/assets/${asset.slug}`}>
      <Card className="overflow-hidden hover:shadow-md transition-shadow">
        <CardContent className="p-0">
          {/* Photo Thumbnail */}
          <AssetPhotoThumbnail assetId={asset.id} address={asset.address} />
          {/* Asset Info */}
          <div className="bg-gray-100 p-3 flex justify-between items-center">
            <div className="flex items-center">
              <span className="font-medium">{asset.name}</span>
            </div>
            <Badge
              variant={
                asset.investment_status === 'active' ? 'default' : 'secondary'
              }
            >
              {asset.investment_status}
            </Badge>
          </div>
          <div className="p-4">
            {asset.address && (
              <p className="text-sm text-gray-600 mb-2">{asset.address}</p>
            )}
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">{asset.asset_type}</span>
              {asset.market_value && (
                <span className="text-sm font-medium">
                  {formatCurrency(asset.market_value)}
                </span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};
